<template>
  <AuthLayout
    mode="register"
    sidebar-title="Register your practice"
    sidebar-text="Join RemoniHealth and stay connected to your patients."
    :support-phone="supportPhone"
    :progress-steps="practiceSteps"
    :progress-current-step="1"
    title="Practice Registration"
    :subtitle="classic ? 'Step 1. Registration.' : 'Tell us about your practice.'"
    :classic="classic"
  >
    <template v-if="classic" #classic-subtitle>
      <span class="classic-auth__accent">Step 1.</span> Registration.
    </template>

    <form ref="formRef" class="auth-register__form" novalidate @submit.prevent="handleSubmit">

      <div class="auth-register__field-with-link">
        <AuthField
          id="npi"
          v-model="form.npi"
          label="NPI Number"
          type="text"
          input-mode="numeric"
          :max-length="10"
          input-mask="digits"
          autocomplete="off"
          :placeholder="classic ? '' : '10-digit NPI'"
          :error="displayFieldErrors.npi || (npiError && !isLookingUp ? npiError : '')"
          :hint="isLookingUp ? 'Looking up NPI…' : ''"
        />
        <a :href="npiSearchLink" target="_blank" rel="noopener" class="auth-login__link">
          {{ classic ? npiSearchLink : 'NPI lookup' }}
        </a>
      </div>

      <Transition name="npi-result">
        <div v-if="npiResult && !classic" class="npi-result-card" role="status" aria-live="polite">
          <span class="npi-result-card__icon" aria-hidden="true">✓</span>
          <div class="npi-result-card__body">
            <p class="npi-result-card__name">{{ providerDisplayName }}</p>
            <p v-if="npiResult.credential" class="npi-result-card__meta">{{ npiResult.credential }}</p>
            <p v-if="npiResult.status" class="npi-result-card__status" :class="npiResult.status === 'A' ? 'npi-result-card__status--active' : 'npi-result-card__status--inactive'">
              {{ npiResult.status === 'A' ? 'Active' : 'Inactive' }}
            </p>
          </div>
          <button
            type="button"
            class="npi-result-card__clear"
            aria-label="Clear NPI lookup"
            @click="clearNpiResult"
          >✕</button>
        </div>
      </Transition>

      <AuthField
        id="first-name"
        v-model="form.first_name"
        label="First Name *"
        autocomplete="given-name"
        input-mask="name"
        :error="displayFieldErrors.first_name"
        required
      />

      <AuthField
        id="last-name"
        v-model="form.last_name"
        label="Last Name *"
        autocomplete="family-name"
        input-mask="name"
        :error="displayFieldErrors.last_name"
        required
      />

      <AuthField
        id="phone-number"
        v-model="form.phone_number"
        :label="classic ? 'Doctor Mobile *' : 'Mobile Phone *'"
        type="tel"
        input-mask="phone"
        placeholder="000-000-0000"
        autocomplete="tel"
        :error="displayFieldErrors.phone_number"
        required
      />

      <AuthField
        id="mobile"
        v-model="form.mobile"
        label="Practice Phone Number *"
        type="tel"
        input-mask="phone"
        placeholder="000-000-0000"
        autocomplete="tel"
        :error="displayFieldErrors.mobile"
        required
      />

      <AuthField
        id="practice-name"
        v-model="form.practice_name"
        label="Practice Name *"
        autocomplete="organization"
        :error="displayFieldErrors.practice_name"
        required
      />

      <AuthField
        id="practice-address"
        ref="practiceAddressRef"
        v-model="form.practice_address"
        :label="classic ? 'Practice Address (Line 1) *' : 'Practice Address *'"
        :placeholder="classic ? 'Enter Street Address' : ''"
        :autocomplete="googlePlacesEnabled ? 'off' : 'street-address'"
        :hint="googlePlacesEnabled ? 'Start typing to search with Google Maps' : ''"
        :error="displayFieldErrors.practice_address"
        required
      />

      <AuthField
        id="practice-address-additional"
        ref="practiceAddressAdditionalRef"
        v-model="form.practice_address_additional"
        :label="classic ? 'Practice Address (Line 2)' : 'Address (Line 2)'"
        :placeholder="classic ? 'Address line 2 (example: Suite, Building, Apartment)' : ''"
        autocomplete="address-line2"
        :error="displayFieldErrors.practice_address_additional"
      />

      <AuthField
        id="city"
        v-model="form.city"
        label="City *"
        autocomplete="address-level2"
        :error="displayFieldErrors.city"
        required
      />

      <AuthField
        id="state"
        v-model="form.state"
        label="State *"
        autocomplete="address-level1"
        :error="displayFieldErrors.state"
        required
      />

      <AuthField
        id="zip-code"
        v-model="form.zip_code"
        :label="classic ? 'Zip code *' : 'Zip Code *'"
        placeholder="12345 or 12345-6789"
        autocomplete="postal-code"
        :error="displayFieldErrors.zip_code"
        required
      />

      <AuthField
        id="country"
        v-model="form.country"
        label="Country *"
        autocomplete="country-name"
        :error="displayFieldErrors.country"
        required
      />

      <AuthField
        id="email"
        v-model="form.email"
        label="Email *"
        type="email"
        autocomplete="email"
        :error="displayFieldErrors.email"
        required
      />

      <AuthPasswordField
        id="password"
        v-model="form.password"
        label="Password *"
        :error="displayFieldErrors.password || passwordError"
        :classic="classic"
        required
      />
      <AuthPasswordMeter
        :password="form.password"
        :email="form.email"
        :first-name="form.first_name"
        :last-name="form.last_name"
      />

      <AuthPasswordField
        id="password-confirm"
        v-model="form.password_confirmation"
        label="Confirm Password *"
        :error="displayFieldErrors.password_confirmation || passwordConfirmationError"
        :classic="classic"
        required
      />

      <span v-if="displayFormError" class="auth-login__error-block" role="alert">{{ displayFormError }}</span>

      <div class="auth-login__actions">
        <AuthButton type="submit" :loading="loading">Register</AuthButton>
      </div>
    </form>

    <template v-if="!classic" #footer>
      <p class="auth-login__signup-hint">
        Already have an account?
        <a :href="loginUrl" class="auth-login__link auth-login__link--plain">Sign in</a>
      </p>
    </template>
  </AuthLayout>
</template>

<script setup>
import { computed, nextTick, onMounted, reactive, ref, toRef, watch } from 'vue'
import AuthLayout from '../../shared/components/AuthLayout.vue'
import AuthField from '../../shared/components/AuthField.vue'
import AuthPasswordField from '../../shared/components/AuthPasswordField.vue'
import AuthPasswordMeter from '../../shared/components/AuthPasswordMeter.vue'
import {
  isPasswordValid,
  PASSWORD_CONFIRMATION_MISMATCH_MESSAGE,
} from '../../shared/utils/passwordPolicy.js'
import AuthButton from '../../shared/components/AuthButton.vue'
import { PRACTICE_STEPS } from '../constants/steps.js'
import { useGooglePlaces } from '../composables/useGooglePlaces.js'
import { NPI_FORMAT_MESSAGE, useNpiLookup } from '../composables/useNpiLookup.js'

const props = defineProps({
  loading:      { type: Boolean, default: false },
  formError:    { type: String, default: '' },
  fieldErrors:  { type: Object, default: () => ({}) },
  loginUrl:     { type: String, default: '/login' },
  supportPhone: { type: String, default: '815-908-2020' },
  mapsApiKey:   { type: String, default: '' },
  googlePlacesEnabled: { type: Boolean, default: false },
  npiSearchLink: { type: String, default: 'https://npiregistry.cms.hhs.gov/search' },
  classic:       { type: Boolean, default: false },
})

const emit = defineEmits(['submit'])

const practiceSteps = PRACTICE_STEPS

const formRef = ref(null)
const practiceAddressRef = ref(null)
const practiceAddressAdditionalRef = ref(null)
const passwordError = ref('')
const passwordConfirmationError = ref('')
const clientFormError = ref('')
const clientFieldErrors = reactive({})

const form = reactive({
  npi: '',
  first_name: '',
  last_name: '',
  phone_number: '',
  mobile: '',
  practice_name: '',
  practice_address: '',
  practice_address_additional: '',
  city: '',
  state: '',
  country: '',
  zip_code: '',
  email: '',
  password: '',
  password_confirmation: '',
})

Object.keys(form).forEach(key => {
  watch(() => form[key], () => {
    delete clientFieldErrors[key]
    if (props.fieldErrors[key]) {
      props.fieldErrors[key] = ''
    }
  })
})

const npiRef = toRef(form, 'npi')

const { isLookingUp, npiResult, npiError } = useNpiLookup(
  npiRef,
  (provider) => {
    if (provider.first_name) form.first_name = provider.first_name
    if (provider.last_name) form.last_name = provider.last_name
    if (provider.practice_address) form.practice_address = provider.practice_address
    if (provider.city) form.city = provider.city
    if (provider.state) form.state = provider.state
    if (provider.zip_code) form.zip_code = provider.zip_code
    if (provider.country) form.country = provider.country
    if (provider.phone) {
      form.mobile = provider.phone
    }
    if (provider.organization_name) {
      form.practice_name = provider.organization_name
    }
  },
  () => {},
)

const providerDisplayName = computed(() => {
  if (!npiResult.value) return ''
  const p = npiResult.value
  if (p.organization_name) return p.organization_name
  return [p.first_name, p.last_name].filter(Boolean).join(' ')
})

function clearNpiResult() {
  form.npi = ''
}

const { initAutocomplete } = useGooglePlaces(toRef(props, 'mapsApiKey'))

onMounted(async () => {
  if (!props.googlePlacesEnabled) return

  await nextTick()

  const addressInput = practiceAddressRef.value?.getInputElement?.()
  if (addressInput) {
    await initAutocomplete(addressInput, (place) => {
      Object.assign(form, place)
    })
  }

  const additionalInput = practiceAddressAdditionalRef.value?.getInputElement?.()
  if (additionalInput) {
    await initAutocomplete(additionalInput, (place) => {
      form.practice_address_additional = place.practice_address_additional
    }, { addressOnly: true })
  }
})

const passwordContext = computed(() => ({
  email: form.email,
  firstName: form.first_name,
  lastName: form.last_name,
}))

const displayFormError = computed(() => props.formError || clientFormError.value)
const phoneNumberFormatError = computed(() => (
  form.phone_number && !/^\d{3}-\d{3}-\d{4}$/.test(form.phone_number)
    ? 'Enter a valid 10-digit phone number.'
    : ''
))
const displayFieldErrors = computed(() => ({
  ...props.fieldErrors,
  ...clientFieldErrors,
  ...(phoneNumberFormatError.value ? { phone_number: phoneNumberFormatError.value } : {}),
}))

const fieldIds = {
  npi: 'npi',
  first_name: 'first-name',
  last_name: 'last-name',
  phone_number: 'phone-number',
  mobile: 'mobile',
  practice_name: 'practice-name',
  practice_address: 'practice-address',
  practice_address_additional: 'practice-address-additional',
  city: 'city',
  state: 'state',
  zip_code: 'zip-code',
  country: 'country',
  email: 'email',
  password: 'password',
  password_confirmation: 'password-confirm',
}

const fieldsById = Object.fromEntries(
  Object.entries(fieldIds).map(([field, id]) => [id, field]),
)

function collectNativeValidationErrors() {
  formRef.value?.querySelectorAll('input').forEach(input => {
    const field = fieldsById[input.id]
    if (field && !input.checkValidity()) {
      clientFieldErrors[field] = input.validationMessage
    }
  })

  if (form.mobile && !/^\d{3}-\d{3}-\d{4}$/.test(form.mobile)) {
    clientFieldErrors.mobile = 'Enter a valid 10-digit phone number.'
  }

  if (form.npi && !/^\d{10}$/.test(form.npi)) {
    clientFieldErrors.npi = NPI_FORMAT_MESSAGE
  }

  if (phoneNumberFormatError.value) {
    clientFieldErrors.phone_number = phoneNumberFormatError.value
  }

  if (form.zip_code && !/^\d{5}(?:-\d{4})?$/.test(form.zip_code)) {
    clientFieldErrors.zip_code = 'Enter a 5-digit ZIP code or ZIP+4 (12345-6789).'
  }
}

async function focusField(field) {
  await nextTick()
  const id = fieldIds[field] || field
  const input = formRef.value?.querySelector(`#${id}`)
  input?.focus()
  input?.scrollIntoView({ behavior: 'smooth', block: 'center' })
}

watch(() => props.fieldErrors, (errors) => {
  const firstErrorField = Object.keys(errors || {})[0]
  if (firstErrorField) focusField(firstErrorField)
})

watch(() => form.password, () => {
  passwordError.value = ''
  clientFormError.value = ''
})

watch(() => form.password_confirmation, () => {
  passwordConfirmationError.value = ''
  clientFormError.value = ''
})

const handleSubmit = async () => {
  passwordError.value = ''
  passwordConfirmationError.value = ''
  clientFormError.value = ''
  Object.keys(clientFieldErrors).forEach(field => delete clientFieldErrors[field])

  collectNativeValidationErrors()

  if (!form.password) {
    passwordError.value = 'Password is required.'
    clientFieldErrors.password = passwordError.value
  } else if (!isPasswordValid(form.password, passwordContext.value)) {
    passwordError.value = 'Password does not meet the requirements below.'
    clientFieldErrors.password = passwordError.value
  }

  if (!form.password_confirmation) {
    passwordConfirmationError.value = 'Confirm password is required.'
    clientFieldErrors.password_confirmation = passwordConfirmationError.value
  } else if (form.password !== form.password_confirmation) {
    passwordConfirmationError.value = PASSWORD_CONFIRMATION_MISMATCH_MESSAGE
    clientFieldErrors.password_confirmation = passwordConfirmationError.value
  }

  const firstErrorField = Object.keys(clientFieldErrors)[0]
  if (firstErrorField) {
    clientFormError.value = 'Please correct the highlighted fields before registering.'
    await focusField(firstErrorField)
    return
  }

  emit('submit', { ...form })
}
</script>

<style scoped>
.npi-result-card {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  background: var(--auth-npi-bg, #ecfdf5);
  border: 1px solid var(--auth-npi-border, #6ee7b7);
  margin-bottom: 0.25rem;
}

.npi-result-card__icon {
  flex-shrink: 0;
  font-size: 1rem;
  line-height: 1.4;
  color: var(--auth-npi-icon, #059669);
  font-weight: 700;
}

.npi-result-card__body {
  flex: 1;
  min-width: 0;
}

.npi-result-card__name {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--auth-npi-name, #065f46);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.npi-result-card__meta {
  margin: 0.125rem 0 0;
  font-size: 0.75rem;
  color: var(--auth-npi-meta, #047857);
}

.npi-result-card__status {
  display: inline-block;
  margin: 0.25rem 0 0;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0.125rem 0.375rem;
  border-radius: 9999px;
}

.npi-result-card__clear {
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--auth-npi-meta, #047857);
  font-size: 0.8rem;
  line-height: 1;
  padding: 0.125rem;
  opacity: 0.6;
  transition: opacity 0.15s;
}

.npi-result-card__clear:hover {
  opacity: 1;
}

</style>
